import React from "react";
import "./AnalyticsAskEden.css";

/*
 * The canonical analytics Ask EDEN command bar.
 * Keep this file byte-identical in Client (Cortex) and Superadmin (Quantum).
 *
 * Every analytics surface that talks to EDEN renders THIS component. Pages
 * supply behaviour — the value, the handlers, their own contextual placeholder
 * — and nothing else. Geometry, typography, the spark, the submit control and
 * every interaction state belong to the component and its stylesheet, so a
 * page cannot make its bar taller, rounder or differently lettered than the
 * one beside it.
 *
 * WHY THE MARKUP LIVES HERE TOO, NOT JUST THE CSS
 * -----------------------------------------------
 * A shared stylesheet alone would not have caught what was actually wrong. The
 * two pre-existing copies of this bar had already diverged in their MARKUP as
 * well as their rules: one input carried `type="text"` and the other did not,
 * one form was labelled for assistive technology and the other was anonymous.
 * Owning the element tree closes that seam — there is one input, one button,
 * one label contract, and a page can only vary what it is handed props for.
 *
 * CONTROLLED AND UNCONTROLLED, BOTH
 * ---------------------------------
 * The two callers genuinely differ here and neither is wrong.
 * PlatformAnalyticsExperience holds the draft command in React state because
 * its EDEN parser re-reads it across renders; Platform Analytics keeps the
 * field uncontrolled and reads it from a ref on submit. Forcing either into
 * the other's shape would have meant rewriting working command handling for a
 * visual task, so the component supports both: pass `value` (with `onChange`)
 * for the controlled form, or pass `inputRef` and leave `value` undefined for
 * the uncontrolled one. `value === undefined` is the discriminator React
 * itself uses, so there is no third state to get wrong.
 */
export default function AnalyticsAskEden({
  value,
  onChange,
  onSubmit,
  placeholder,
  inputLabel,
  submitLabel = "Send EDEN command",
  inputRef,
  disabled = false,
  loading = false,
  answer = null,
  onDismissAnswer,
  answerDismissLabel = "Dismiss EDEN answer",
}) {
  const controlled = value !== undefined;

  return (
    <>
      <form
        className="cphere-analytics-ask-eden"
        onSubmit={onSubmit}
        aria-label="Ask EDEN"
        aria-busy={loading || undefined}
      >
        <span className="cphere-analytics-ask-eden__icon" aria-hidden="true">
          ✦
        </span>
        <input
          type="text"
          className="cphere-analytics-ask-eden__input"
          ref={inputRef}
          /* Controlled and uncontrolled are mutually exclusive in React: passing
             `value={undefined}` alongside a ref is what makes the field
             uncontrolled, and passing both a value and a ref is legal. */
          {...(controlled ? { value, onChange } : {})}
          placeholder={placeholder}
          aria-label={inputLabel}
          disabled={disabled}
        />
        <button
          type="submit"
          className="cphere-analytics-ask-eden__submit"
          aria-label={submitLabel}
          disabled={disabled || loading}
        >
          ⏎
        </button>
      </form>
      {answer ? (
        <p className="cphere-analytics-ask-eden-answer" role="status">
          {answer}
          <button
            type="button"
            className="cphere-analytics-ask-eden-answer__dismiss"
            onClick={onDismissAnswer}
            aria-label={answerDismissLabel}
          >
            ×
          </button>
        </p>
      ) : null}
    </>
  );
}
